import { useState, useRef, useEffect, useLayoutEffect } from 'react'
import MarkdownMessage from './MarkdownMessage'

export interface PanelMessage {
  id: string
  sender: 'user' | 'ai'
  message: string
}

// 1行時の入力欄の高さ。送信/停止ボタンもこの高さに合わせる。
const INPUT_ROW_HEIGHT = 40

export interface ChatPanelProps {
  messages: PanelMessage[]
  isAiTyping: boolean
  inputValue: string
  onInputChange: (value: string) => void
  onSend: () => void
  isSending?: boolean
  onStop?: () => void
  className?: string
  placeholder?: string
}

const ChatPanel = ({
  messages,
  isAiTyping,
  inputValue,
  onInputChange,
  onSend,
  isSending = false,
  onStop,
  className = '',
  placeholder = 'メッセージを入力...',
}: ChatPanelProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const scrollBeforeKeyboard = useRef<number | null>(null)
  const keyboardOpenRef = useRef(false)

  // Fit the textarea to its text. Runs on every value change (not just on input events)
  // so it also shrinks back to one row when the parent clears the value after sending.
  useLayoutEffect(() => {
    if (!isExpanded && textareaRef.current) {
      const el = textareaRef.current
      el.style.height = 'auto'
      el.style.height = Math.min(el.scrollHeight, 168) + 'px'
    }
  }, [inputValue, isExpanded])

  // iPad Safari scrolls the page up to keep the textarea above the on-screen keyboard and
  // leaves it there when the keyboard closes, hiding most of the chat. When the visual
  // viewport grows back, return to where the page was before the textarea was focused.
  // The layout viewport (clientHeight) doesn't shrink for the keyboard on iOS, so a much
  // smaller visual viewport means the keyboard is open; PCs never get here.
  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return
    const handleResize = () => {
      // Pinch-zoom shrinks the visual viewport too, so only count it while typing here.
      const open = vv.height < document.documentElement.clientHeight - 150
      if (open && document.activeElement === textareaRef.current) {
        keyboardOpenRef.current = true
      } else if (!open && keyboardOpenRef.current) {
        keyboardOpenRef.current = false
        const saved = scrollBeforeKeyboard.current
        scrollBeforeKeyboard.current = null
        if (saved !== null) window.scrollTo({ top: saved, behavior: 'smooth' })
      }
    }
    vv.addEventListener('resize', handleResize)
    return () => vv.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (messages.length === 0 || !containerRef.current) return
    const lastMessage = messages[messages.length - 1]
    const container = containerRef.current

    if (lastMessage.sender === 'user' || messages.length === 1) {
      setTimeout(() => {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' })
      }, 100)
    } else if (lastMessage.sender === 'ai') {
      const el = container.querySelector(`[data-message-id="${lastMessage.id}"]`)
      if (el) {
        setTimeout(() => {
          const containerRect = container.getBoundingClientRect()
          const messageRect = el.getBoundingClientRect()
          container.scrollTo({
            top: container.scrollTop + (messageRect.top - containerRect.top) - 16,
            behavior: 'smooth'
          })
        }, 100)
      }
    }
  }, [messages])

  const showStopButton = (isSending || isAiTyping) && onStop
  const sendButtonBase = 'self-end px-4 rounded-lg text-sm font-medium transition-colors'

  return (
    <div className={`flex-1 flex flex-col min-h-0 ${className}`}>
      {/* Messages area */}
      <div
        ref={containerRef}
        className={`${isExpanded ? 'hidden' : 'flex-1 min-h-0'} overflow-y-auto p-4 space-y-4 bg-theme-tertiary-pale`}
      >
        {messages.map((msg) => (
          <div
            key={msg.id}
            data-message-id={msg.id}
            className={`flex ${msg.sender === 'ai' ? 'justify-start' : 'justify-end'}`}
          >
            <div
              className={`max-w-[85%] rounded-lg p-3 text-sm ${
                msg.sender === 'ai'
                  ? 'bg-theme-tertiary-medium text-black shadow-sm border border-theme-tertiary/20'
                  : 'bg-theme-tertiary text-white'
              }`}
            >
              {msg.sender === 'ai' ? (
                // AI replies can contain Markdown (lists, bold, headings) — render it.
                // The student's own text stays literal so typed symbols aren't reinterpreted.
                <MarkdownMessage content={msg.message} />
              ) : (
                <div className="whitespace-pre-wrap text-left">{msg.message}</div>
              )}
            </div>
          </div>
        ))}

        {isAiTyping && (
          <div className="flex justify-start">
            <div className={`max-w-[85%] rounded-lg p-3 text-sm bg-theme-tertiary-medium text-black shadow-sm border border-theme-tertiary/20`}>
              <div className="flex items-center space-x-1">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-theme-tertiary rounded-full animate-bounce" />
                  <div className="w-2 h-2 bg-theme-tertiary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                  <div className="w-2 h-2 bg-theme-tertiary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                </div>
                <span className="text-xs text-gray-500 ml-2">ミラパス君が考え中...</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input area */}
      <div className={`border-t p-4 ${isExpanded ? 'flex-1 flex flex-col min-h-0' : ''}`}>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (!isSending && inputValue.trim()) {
              setIsExpanded(false)
              onSend()
            }
          }}
          className={`flex space-x-2 ${isExpanded ? 'flex-1 min-h-0' : ''}`}
        >
          <div className={`relative flex-1 min-w-0 ${isExpanded ? 'flex flex-col min-h-0' : ''}`}>
            <button
              type="button"
              onClick={() => { setIsExpanded(v => !v); setTimeout(() => textareaRef.current?.focus(), 0) }}
              className="absolute top-1.5 right-1.5 z-10 text-gray-400 hover:text-gray-600 transition-colors"
              title={isExpanded ? '縮小' : '拡大'}
            >
              {isExpanded ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="4 14 10 14 10 20" />
                  <polyline points="20 10 14 10 14 4" />
                  <line x1="10" y1="14" x2="3" y2="21" />
                  <line x1="21" y1="3" x2="14" y2="10" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 3 21 3 21 9" />
                  <polyline points="9 21 3 21 3 15" />
                  <line x1="21" y1="3" x2="14" y2="10" />
                  <line x1="3" y1="21" x2="10" y2="14" />
                </svg>
              )}
            </button>
            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => onInputChange(e.target.value)}
              placeholder={placeholder}
              rows={1}
              className={`block w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-theme-tertiary focus:border-transparent resize-none overflow-y-auto ${isExpanded ? 'flex-1 min-h-0' : ''}`}
              style={isExpanded ? { height: '100%' } : { minHeight: INPUT_ROW_HEIGHT, maxHeight: '168px' }}
              onFocus={() => {
                // Keep the first position of a typing session; refocusing while the
                // keyboard is still open would otherwise save the already-shifted one.
                if (scrollBeforeKeyboard.current === null) scrollBeforeKeyboard.current = window.scrollY
              }}
              onBlur={() => {
                // No on-screen keyboard came up (PC, hardware keyboard): nothing to restore.
                if (!keyboardOpenRef.current) scrollBeforeKeyboard.current = null
              }}
            />
          </div>
          {showStopButton ? (
            <button
              type="button"
              onClick={onStop}
              className={`${sendButtonBase} bg-red-500 hover:bg-red-600 text-white`}
              style={{ height: INPUT_ROW_HEIGHT }}
            >
              停止
            </button>
          ) : (
            <button
              type="submit"
              disabled={!inputValue.trim()}
              className={`${sendButtonBase} bg-theme-tertiary hover:bg-theme-tertiary/90 text-white disabled:bg-gray-300 disabled:cursor-not-allowed`}
              style={{ height: INPUT_ROW_HEIGHT }}
            >
              送信
            </button>
          )}
        </form>
      </div>
    </div>
  )
}

export default ChatPanel
